import * as React from 'react'
import { Input } from '@/components/ui/input'
import { useT } from '@/components/visitone/visitone-provider'
import { formatDateInput } from '@/lib/format/tenant-date'
import { cn } from '@/lib/utils'

/*
 * Field: enforces the contract's field order — label, optional help, control,
 * error — and wires the control's id, aria-describedby and aria-invalid.
 * Required is marked; optional is not.
 */
function Field({
  id,
  label,
  help,
  error,
  required,
  className,
  children,
}: {
  id: string
  label: string
  help?: string
  error?: string
  required?: boolean
  className?: string
  children: React.ReactElement<Record<string, unknown>>
}) {
  const helpId = help ? `${id}-help` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined

  const control = React.cloneElement(children, {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
  })

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={id}
        className="text-sm text-fg"
        style={{ fontWeight: 'var(--vo-label-font-weight)' }}
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-danger">
            *
          </span>
        ) : null}
      </label>
      {help ? (
        <p id={helpId} className="max-w-prose text-xs text-fg-muted">
          {help}
        </p>
      ) : null}
      {control}
      {error ? (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/*
 * FieldGroup: the same contract for a group of controls, such as checkboxes.
 * The label is a <legend>, so every control in the <fieldset> is announced
 * with it; help and error describe the group. The caller lays out the
 * controls, and moves focus to the first one when the error is jumped to.
 */
function FieldGroup({
  id,
  legend,
  help,
  error,
  required,
  className,
  children,
}: {
  id: string
  legend: string
  help?: string
  error?: string
  required?: boolean
  className?: string
  children: React.ReactNode
}) {
  const helpId = help ? `${id}-help` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <fieldset id={id} aria-describedby={describedBy} className={cn('min-w-0', className)}>
      <legend className="mb-1.5 text-sm text-fg" style={{ fontWeight: 'var(--vo-label-font-weight)' }}>
        {legend}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-danger">
            *
          </span>
        ) : null}
      </legend>
      <div className="flex flex-col gap-1.5">
        {help ? (
          <p id={helpId} className="max-w-prose text-xs text-fg-muted">
            {help}
          </p>
        ) : null}
        {error ? (
          <p id={errorId} className="text-xs font-medium text-danger">
            {error}
          </p>
        ) : null}
        {children}
      </div>
    </fieldset>
  )
}

/*
 * DateField: a Field whose control is a calendar date typed in the tenant's
 * pattern (TECH-DEBT(VIS-327): en-GB dd/mm/yyyy until the tenant formatter
 * exists), never a native date input, which shows the browser's locale. The
 * help ends with an example in that pattern. `value` is the text as typed,
 * kept even when it isn't a date; the caller reads it with `parseDateInput`.
 */
function DateField({
  id,
  label,
  help,
  error,
  required,
  example,
  value,
  onChange,
  inputRef,
  className,
}: {
  id: string
  label: string
  help?: string
  error?: string
  required?: boolean
  /** An ISO calendar date shown as the example, such as today. */
  example: string
  value: string
  onChange: (text: string) => void
  inputRef?: React.Ref<HTMLInputElement>
  className?: string
}) {
  const t = useT()
  const shown = formatDateInput(example)
  return (
    <Field
      id={id}
      label={label}
      help={help ? t('core.dateField.helpWithExample', { help, example: shown }) : t('core.dateField.example', { example: shown })}
      error={error}
      required={required}
      className={className}
    >
      <Input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        aria-required={required || undefined}
        className="tabular-nums"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  )
}

export { DateField, Field, FieldGroup }
